// invest_FE/src/components/verification/PhoneVerification.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { apiFetch } from '../../utils/apiClient';
import { BASE_URL } from '../../utils/config';
import './PhoneVerification.css';

const PhoneVerification = ({ initialPhoneNumber, initialCountryCode }) => {
  const { user, updateUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const { t } = useTranslation();

  const getFullNumberFromProps = () => {
    const raw = (initialCountryCode || '') + (initialPhoneNumber || '');
    if (!raw) return '';
    return raw.replace('+', '');
  };

  const [phone, setPhone] = useState(getFullNumberFromProps);
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [countryCode, setCountryCode] = useState('us');
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimer = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  useEffect(() => {
    const rawPhoneNumber = (initialCountryCode || '') + (initialPhoneNumber || '');
    if (rawPhoneNumber) {
      // This effect should only respond to prop changes, not internal state changes.
      // We set the phone state directly from the props.
      setPhone(rawPhoneNumber.replace('+', ''));
      const phoneNumberInstance = parsePhoneNumberFromString(rawPhoneNumber.startsWith('+') ? rawPhoneNumber : `+${rawPhoneNumber}`);
      if (phoneNumberInstance?.country) {
        setCountryCode(phoneNumberInstance.country.toLowerCase());
      }
    }
  }, [initialPhoneNumber, initialCountryCode]);

  useEffect(() => {
    if (initialPhoneNumber || initialCountryCode) return;
    const fetchCountryCode = async () => {
      try {
        // const response = await fetch('https://ipapi.co/json/');
        const response = await apiFetch(`${BASE_URL}/api/general/country-from-ip`);
        if (!response.ok) throw new Error(`Failed to fetch IP geolocation data: ${response.status}`);
        const data = await response.json();
        // if (data && data.country_code) {
        if (data && data.country) {
          setCountryCode(data.country.toLowerCase());
        }
      } catch (error) {
        console.warn('Could not automatically fetch country code, defaulting to US.', error);
      }
    };
    fetchCountryCode();
  }, [initialPhoneNumber, initialCountryCode]);

  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownTimer.current = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current);
    };
  }, [resendCooldown]);

  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    setIsLoading(true);
    if (!phone || phone.length < 10) {
      addNotification(t('phoneVerification.enterValidNumber'), 'error');
      setIsLoading(false);
      return;
    }
    try {
      if (recaptchaVerifierRef.current) recaptchaVerifierRef.current.clear();
      const recaptchaContainer = document.getElementById('recaptcha-container');
      if (recaptchaContainer) recaptchaContainer.innerHTML = '';
      recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: (response) => console.log('reCAPTCHA solved'),
        'expired-callback': () => {
          addNotification(t('phoneVerification.recaptchaExpired'), 'warn');
          if (recaptchaVerifierRef.current) recaptchaVerifierRef.current.clear();
        },
      });
      const result = await signInWithPhoneNumber(auth, `+${phone}`, recaptchaVerifierRef.current);
      setConfirmationResult(result);
      setStep(2);
      setResendCooldown(30);
      addNotification(t('phoneVerification.codeSent'), 'success');
    } catch (error) {
      console.error('Error sending OTP:', error);
      addNotification(t('phoneVerification.sendCodeFailed', { error: error.message }), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!otp || otp.length !== 6) {
      addNotification(t('phoneVerification.enter6DigitCode'), 'error');
      setIsLoading(false);
      return;
    }
    try {
      const credential = await confirmationResult.confirm(otp);
      const idToken = await credential.user.getIdToken();
      const response = await apiFetch(`${BASE_URL}/authUser/verify-phone`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ idToken }) }, user.token);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || t('phoneVerification.serverVerificationFailed'));
      }
      const { user: updatedUserData } = await response.json();
      updateUser(updatedUserData);
      addNotification(t('phoneVerification.verifiedSuccessfully'), 'success');
      setStep(1);
      setOtp('');
      setIsEditing(false); // Exit editing mode to show the verified display
    } catch (error) {
      console.error('Error verifying OTP:', error);
      let errorMessage;
      if (error.code === 'auth/invalid-verification-code') {
        errorMessage = t('phoneVerification.incorrectCode');
        addNotification(errorMessage, 'error', 0, true);
      } else if (error.code === 'auth/code-expired') {
        errorMessage = t('phoneVerification.codeExpired');
        addNotification(errorMessage, 'error');
      } else {
        errorMessage = t('phoneVerification.verificationFailed', { error: error.message });
        addNotification(errorMessage, 'error', 0, true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (value, countryData) => {
    setPhone(value);
    setCountryCode(countryData.countryCode);
  };

  const handleChangeNumber = () => {
    setStep(1);
    setOtp('');
    setIsEditing(true); // Go directly back to editing mode
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    const rawPhoneNumber = (initialCountryCode || '') + (initialPhoneNumber || '');
    if (rawPhoneNumber) {
      setPhone(rawPhoneNumber.replace('+', ''));
      const phoneNumberInstance = parsePhoneNumberFromString(rawPhoneNumber.startsWith('+') ? rawPhoneNumber : `+${rawPhoneNumber}`);
      if (phoneNumberInstance?.country) {
        setCountryCode(phoneNumberInstance.country.toLowerCase());
      }
    } else {
      setPhone('');
    }
  };

  const phoneNumberInstance = phone ? parsePhoneNumberFromString(`+${phone}`) : null;
  const formattedNumberForDisplay = phoneNumberInstance ? phoneNumberInstance.format('INTERNATIONAL') : '';

  return (
    <div className="phone-verification-container">
      <h4>{user.isPhoneVerified && !isEditing ? t('phoneVerification.verifiedTitle') : t('phoneVerification.unverifiedTitle')}</h4>
      <p>{user.isPhoneVerified && !isEditing ? t('phoneVerification.verifiedDescription') : t('phoneVerification.unverifiedDescription')}</p>

      {/* Show editing form only if isEditing is true AND we are not in the OTP step */}
      {isEditing && step !== 2 ? (
        <form onSubmit={handleSendOtp} className="phone-verification-form">
          <PhoneInput
            key={`phone-input-editing`}
            country={countryCode}
            value={phone}
            onChange={handlePhoneChange}
            inputProps={{
              name: 'phone',
              required: true,
              autoFocus: true,
            }}
            containerClass="phone-input-container"
            inputClass="phone-input-field"
          />
          <div className="otp-actions">
            <button type="button" onClick={handleCancelClick} className="btn-secondary">
              {t('phoneVerification.buttonCancel')}
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? t('phoneVerification.buttonSending') : t('phoneVerification.buttonSendCode')}
            </button>
          </div>
        </form>
      ) : (
        // VIEW MODE: Displaying the number (verified or not)
        <div className="phone-verified-display">
          <div className="custom-phone-display">
            <input type="text" value={formattedNumberForDisplay || t('phoneVerification.noNumberOnFile')} disabled={true} className="custom-phone-input-disabled" />
          </div>
          <button type="button" onClick={handleChangeNumber} className={`change-btn ${!user.isPhoneVerified ? 'standout' : ''}`}>
            {t('phoneVerification.buttonChange')}
          </button>
        </div>
      )}

      {/* VERIFICATION FLOW (only shows if unverified and not in editing mode) */}
      {!user.isPhoneVerified && !isEditing && step === 1 && (
        <div className="unverified-notice">
          <form onSubmit={handleSendOtp} className="phone-verification-form">
            <button type="submit" disabled={isLoading || !phone} className="btn-primary">
              {isLoading ? t('phoneVerification.buttonSending') : t('phoneVerification.buttonSendCode')}
            </button>
          </form>
        </div>
      )}

      {/* Show OTP form when step is 2. This is now independent of verification status or editing mode. */}
      {step === 2 && (
        <div className="unverified-notice">
          <form onSubmit={handleVerifyOtp} className="phone-verification-form">
            <p>{t('phoneVerification.enterCodeSentTo', { phone: phone })}</p>
            <input type="text" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength="6" placeholder={t('phoneVerification.otpPlaceholder')} required autoFocus className="otp-input-field" />
            <div className="otp-actions">
              <button type="button" onClick={handleSendOtp} className="btn-secondary" disabled={resendCooldown > 0 || isLoading}>
                {resendCooldown > 0 ? t('phoneVerification.buttonResendCooldown', { cooldown: resendCooldown }) : t('phoneVerification.buttonResend')}
              </button>
              <button type="submit" disabled={isLoading} className="btn-primary">
                {isLoading ? t('phoneVerification.buttonVerifying') : t('phoneVerification.buttonVerify')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div id="recaptcha-container"></div>
    </div>
  );
};

export default PhoneVerification;
